import { Deserializer } from "jsonapi-serializer";
import { NO_CONTENT } from "../constants/response-status-codes";
import { genericError } from "../models/GenericError";
import { ListQuery } from "../models/ListQuery";
import { Name } from "../models/Name";
import { PaginationModel } from "../models/Pagination";
import PresetCustomField, { PresetCustomFieldWrapper } from "../models/PresetCustomField";
import { APIConnector } from "./api/APIConnector";
import { buildPreset } from "./presetService";
import { createPagination } from "./utilities/paginationBuilder";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";
import { Translator } from "../contexts/translator";

const apiConnector = new APIConnector();

const compileFilters = (profileId, listParameters) => {
    const profileFilter = {
        key: 'profile.id',
        label: 'Profile', // not displayed
        options: [{ label: profileId, value: profileId }],
        selected: [{ label: profileId, value: profileId }]
    };
    const actionSettingTypeFilter = {
        key: 'settingType.stSysname',
        label: 'Setting Type', // not displayed
        options: [{ label: 'ACT_*', value: 'ACT_*' }],
        selected: [{ label: 'ACT_*', value: 'ACT_*' }]
    };
    return getFilterClause(
        [...listParameters?.filters ?? [], profileFilter, actionSettingTypeFilter],
        listParameters?.searchTerm
    );
}

export const getPresetActions = async (profileId: string, translate: Translator, listParameters?: ListQuery): Promise<PresetCustomFieldWrapper> => {
    try {
        const filter: string = compileFilters(profileId, listParameters);
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const settingFields = 'fields[setting]=trnName,settingType,sDist,profile,settingIdToCopy';
        const settingTypeFields = 'fields[settingType]=trnName';
        const url = `setting?include=profile,settingType${paginationString}&${settingFields}&${settingTypeFields}`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const presetAction: PresetCustomField[] = data.map(preset => buildPreset(preset, translate));

                return resolve({ rows: presetAction, pagination });
            })

        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getPresetActionNamesWithPagination = async (listParameters?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filters);
        const url = `setting?include=profile,settingType${paginationString}`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, response: any[]) => {
                if (error) {
                    return reject(error);
                }

                const presetInputs: Name[] = response.map(setting => {
                    return {
                        value: setting['id'],
                        label: setting['trn-name'],
                        sdist: setting['s-dist'],
                        profileId: setting['profile']['id'],
                        settingIdToCopy: setting['id'],
                        columnId: setting['setting-type']['id'],
                    };
                });

                return resolve({ rows: presetInputs, pagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        })
    }
};


export const deletePresetAction = async (id: string): Promise<{ id: string, success: boolean }> => {
    try {
        const url = `setting/${id}`;
        const presetAction = {
            data: {
                type: "setting",
                id: id
            }
        }
        const response = await apiConnector.deleteData(url, presetAction)

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true })
            }

            const json = await response.json();
            return reject({ status: response.status, id: id, success: false, message: getConstraintViolation(json.errors[0].detail) });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};
